import {
  FC,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {endImpersonation, getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => Promise<boolean>
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: async () => true,
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const revokeImpersonationSession = async (
  token: string,
  retryDelays: number[] = [0]
): Promise<boolean> => {
  for (const retryDelay of retryDelays) {
    if (retryDelay > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, retryDelay))
    }

    try {
      await endImpersonation(token)
      return true
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 401 || status === 403) return true
      console.error('IMPERSONATION SESSION REVOKE ERROR', error)
    }
  }

  return false
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    // A deliberate login/account change belongs to the whole browser profile.
    // Drop any tab-local override so the new account takes effect in this tab too.
    authHelper.clearTabAuthState()
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = useCallback(async () => {
    const impersonatedAuth = authHelper.getImpersonatedAuthSnapshot()
    if (impersonatedAuth?.api_token) {
      const revoked = await revokeImpersonationSession(impersonatedAuth.api_token)
      if (!revoked) {
        // Do not discard a still-active target token on a transient failure;
        // the logout screen lets the administrator retry safely.
        return false
      }
    }

    authHelper.removeAuth()
    authHelper.clearTabAuthState()
    authHelper.publishLogout()
    setAuth(undefined)
    setCurrentUser(undefined)
    return true
  }, [])

  useEffect(() => {
    const stopTabLocalSessionAfterGlobalLogout = async (event: StorageEvent) => {
      if (
        event.key !== authHelper.AUTH_LOGOUT_EPOCH_LOCAL_STORAGE_KEY ||
        !event.newValue
      ) {
        return
      }

      const impersonatedAuth = authHelper.getImpersonatedAuthSnapshot()
      authHelper.clearTabAuthState()
      authHelper.removeAuth()
      setAuth(undefined)
      setCurrentUser(undefined)

      if (impersonatedAuth?.api_token) {
        const revoked = await revokeImpersonationSession(
          impersonatedAuth.api_token,
          [0, 1000, 3000]
        )
        if (!revoked) {
          authHelper.setPendingImpersonationRevocation(impersonatedAuth)
        }
      }

      window.location.replace('/auth/login')
    }

    window.addEventListener('storage', stopTabLocalSessionAfterGlobalLogout)
    return () => window.removeEventListener('storage', stopTabLocalSessionAfterGlobalLogout)
  }, [])

  useEffect(() => {
    let revocationInProgress = false
    const retryPendingRevocation = async () => {
      if (revocationInProgress) return
      const pendingRevocation = authHelper.getPendingImpersonationRevocation()
      if (!pendingRevocation?.api_token) return

      revocationInProgress = true
      const revoked = await revokeImpersonationSession(
        pendingRevocation.api_token,
        [0, 1000, 3000]
      )
      if (revoked) authHelper.removePendingImpersonationRevocation()
      revocationInProgress = false
    }

    void retryPendingRevocation()
    const retryWhenVisible = () => {
      if (document.visibilityState === 'visible') void retryPendingRevocation()
    }
    window.addEventListener('online', retryPendingRevocation)
    window.addEventListener('focus', retryPendingRevocation)
    document.addEventListener('visibilitychange', retryWhenVisible)
    return () => {
      window.removeEventListener('online', retryPendingRevocation)
      window.removeEventListener('focus', retryPendingRevocation)
      document.removeEventListener('visibilitychange', retryWhenVisible)
    }
  }, [])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [initializationError, setInitializationError] = useState('')
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data} = await getUserByToken(apiToken)
          if (data && data?.tenantId > 0) {
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          const status = (error as any)?.response?.status
          const administratorAuth = authHelper.getImpersonatorAuth()
          if (administratorAuth?.api_token) {
            if (status === 401 || status === 403) {
              const targetAuth = authHelper.getImpersonatedAuthSnapshot()
              if (
                targetAuth?.api_token === apiToken &&
                authHelper.setTabAuthOverride(administratorAuth)
              ) {
                const revoked = await revokeImpersonationSession(
                  targetAuth.api_token,
                  [0, 1000, 3000]
                )
                if (revoked) {
                  // Restore only the original signed admin token held by this
                  // tab; never copy it to shared localStorage.
                  authHelper.removeImpersonatedAuth()
                  authHelper.removeImpersonatorAuth()
                  window.location.replace('/dashboard')
                  return
                }

                authHelper.removeTabAuthOverride()
              }

              setInitializationError(
                'Kullanıcı geçişi sunucuda güvenli olarak kapatılamadı. Bağlantıyı kontrol edip yeniden deneyin.'
              )
              return
            }

            // A network/server failure does not prove that the target session
            // ended. Keep its token and proof intact so a retry can close it.
            setInitializationError(
              'Kullanıcı oturumu doğrulanamadı. Bağlantıyı kontrol edip yeniden deneyin.'
            )
            return
          }

          const tabAuthOverride = authHelper.getTabAuthOverride()
          if (tabAuthOverride?.api_token === apiToken) {
            if (status === 401 || status === 403) {
              // This tab's restored admin token is no longer valid. Drop only
              // the tab override and fall back to the browser-profile session;
              // never log out a different admin active in another tab.
              authHelper.removeTabAuthOverride()
              window.location.replace('/dashboard')
              return
            }

            setInitializationError(
              'Yönetici oturumu doğrulanamadı. Bağlantıyı kontrol edip yeniden deneyin.'
            )
            return
          }

          const completed = await logout()
          if (!completed) {
            setInitializationError(
              'Kullanıcı geçişi sunucuda kapatılamadı. Bağlantıyı kontrol edip yeniden deneyin.'
            )
          }
        }
      } finally {
        setShowSplashScreen(false)
      }

    }

    if (auth && auth.api_token) {
      requestUser(auth.api_token)
    } else {
      void logout().then((completed) => {
        if (!completed) {
          setInitializationError(
            'Kullanıcı geçişi sunucuda kapatılamadı. Bağlantıyı kontrol edip yeniden deneyin.'
          )
        }
        setShowSplashScreen(false)
      })
    }
    return () => {
      didRequest.current = true
    }
    // eslint-disable-next-line
  }, [])

  if (initializationError) {
    return (
      <div className='d-flex flex-column flex-center min-vh-100 gap-4 p-5 text-center'>
        <div className='alert alert-danger mb-0'>{initializationError}</div>
        <button type='button' className='btn btn-dark' onClick={() => window.location.reload()}>
          Yeniden dene
        </button>
      </div>
    )
  }

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
